/**
 * Programs — V98-3-1. Route: /w/:slug/app/programs (the mobile "Programs"
 * tab and the Programs group in the sidebar). One row per program from
 * GET /programs: stage chips per active period, the derived verdict line
 * and its open work on ONE shared scale (C21). ISO 42001 shows as
 * "Add program" when absent.
 *
 * Reads: GET /api/w/:slug/programs (any member; counts scoped by the
 * server to the caller). Writes: AddProgramAction (owner/admin).
 */
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, PageHeader, SegmentCount, Spinner, StageChip, sharedSegmentMax } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { usePrograms } from '../lib/workspace-navigation';
import { getProgramManifest } from '@aigovops/core';
import { PROGRAM_ORDER, periodStages, programTitle } from '../lib/nav-config';
import { fmtProgramDate } from '../lib/program-shell';
import { AddProgramAction } from '../components/programs/AddProgramAction';
import { CmmcProgramRow } from '../components/today/CmmcCards';
import { isScoredProgram, useCmmcCards } from '../lib/use-cmmc';
import './programs.css';
import './today.css';

export function ProgramsIndex() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const canManage = role === 'owner' || role === 'admin';
  const navigate = useNavigate();
  const q = usePrograms(slug);
  // Manifest order; a planned program the server listed (V102-8-1, staging preview) sorts by its manifest too.
  const rank = (k: string) => getProgramManifest(k)?.order ?? Number.MAX_SAFE_INTEGER;
  const cards = [...(q.data?.programs ?? [])].sort((a, b) => rank(a.program) - rank(b.program));
  const max = sharedSegmentMax(cards.filter((c) => !isScoredProgram(c.program)).map((c) => c.blockers));
  // PR A F6: a scored program (CMMC) renders from its scorecard model, never the generic blocker bar.
  const cmmc = new Map(useCmmcCards(slug, prefix, cards).map((st) => [st.card.profile_id, st] as const));
  const absent = PROGRAM_ORDER.filter((k) => !cards.some((c) => c.program === k));
  // V112-3-1: "Add program" links land on #add — the first program not started yet, focused once the list is there.
  useEffect(() => {
    if (q.isSuccess && window.location.hash === '#add') window.setTimeout(() => { const el = document.getElementById('add'); el?.scrollIntoView?.({ block: 'start' }); el?.focus(); }, 0);
  }, [q.isSuccess]);

  return (
    <div className="gp-programs">
      {/* R3-U19: one header style across the product shell. */}
      <PageHeader variant="app" title="Programs" headingId="programs-h1" />
      {q.isLoading ? <Spinner label="Loading programs" />
        : q.error ? <Alert variant="error"><p>Programs could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
        : (
          <ul className="gp-program-list">
            {cards.map((c) => cmmc.has(c.profile_id) ? (
              <CmmcProgramRow key={c.profile_id} state={cmmc.get(c.profile_id)!} canManage={canManage} />
            ) : (
              <li key={c.profile_id} className="gp-program-row">
                <div className="gp-program-name">
                  <Link to={`${prefix}/programs/${c.program}`}>{programTitle(c.program, c.report_type)}</Link>
                  <span className="gp-program-periods">
                    {c.periods.filter((p) => p.active).map((p, i) => (
                      <span key={`${p.label}:${i}`} className="gp-program-period">
                        {p.label} {periodStages(p).map((st) => <StageChip key={st} stage={st} />)}
                      </span>
                    ))}
                  </span>
                </div>
                <p className="gp-program-headline">{c.headline}</p>
                <SegmentCount
                  overdue={c.blockers.overdue}
                  soon={c.blockers.soon}
                  later={c.blockers.later}
                  max={max}
                  label={`${programTitle(c.program, c.report_type)} ${c.blockers.scope === 'own' ? 'items owed by you' : 'open items'}`}
                />
                {/* R3-U16: the next date and ONE action per program. */}
                <div className="gp-program-next">
                  {c.next_date
                    ? <span>Next <time dateTime={c.next_date.date}>{fmtProgramDate(c.next_date.date)}</time>{` · ${c.next_date.what}`}</span>
                    : <span className="gp-dim">No dates ahead</span>}
                  <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${c.program}`}
                    aria-label={`${c.blockers.overdue ? `Review ${c.blockers.overdue} overdue` : 'Open'} · ${programTitle(c.program, c.report_type)}`}>
                    {c.blockers.overdue ? `Review ${c.blockers.overdue} overdue` : 'Open'}
                  </Link>
                </div>
              </li>
            ))}
            {absent.map((k, i) => (
              <li key={k} className="gp-program-row gp-program-absent" {...(i === 0 ? { id: 'add', tabIndex: -1 } : {})}>
                <div className="gp-program-name">
                  <Link to={`${prefix}/programs/${k}`}>{programTitle(k)}</Link>
                  <span className="gp-program-periods">Not started</span>
                </div>
                {/* V108-8-14: after adding, open the program on what existing sources cover and its next step (no landing on a list). */}
                <div className="gp-program-add"><AddProgramAction slug={slug} program={k} canManage={canManage} variant="secondary" onAdded={() => navigate(`${prefix}/programs/${k}`)} /></div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
